import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {TrackImpression} from '@udemy/event-tracking';
import {DateIntervalType} from '@udemy/graphql';
import {useFormatNumber, useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Button, ButtonProps} from '@udemy/react-core-components';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {PlanComparisonHeader} from '@udemy/react-plan-comparison';
import {Tooltip} from '@udemy/react-popup-components';
import {BlockSkeleton} from '@udemy/react-reveal-components';
import {serverOrClient} from '@udemy/shared-utils';
import {PlanPeriod, PlanPeriodStyle} from '@udemy/subscription-browse';
import {
    AnnualSubscriptionPlanPricingOption,
    ConsumerSubscriptionValuePlan,
    SubscriptionPlanProductType,
    useConsumerSubscriptionPlan,
    useTeamSubscriptionPlan,
} from '@udemy/subscription-graphql';
import {useFormatCurrency, useSiteStats, useUDData, useUDLink} from '@udemy/ud-data';

import {calculateNumCourses, useValuePlanExperimentLogic} from '../../hooks/usePlanComparisonLogic';
import {VALUE_PLANSTARTER_PLAN} from '../../lib/experimentation-constants';
import {
    PERSONAL_PLAN_CHECKOUT_URL,
    PERSONAL_PLAN_UPGRADE_URL,
    removeSubscribeFromPageCookie,
    STARTER_PLAN_CHECKOUT_URL,
    TEAM_PLAN_V1_FALLBACK_CURRENCY_FORMATTER,
} from '../plan-comparison/constants';
import {useComparisonMatrixData, getComparisonMatrixHeaderData} from './comparison-matrix-data';
import styles from './comparison-matrix.module.less';
import {
    trackOrganizationCtaClickEvent,
    trackOrganizationImpression,
    trackSubscriptionCtaClick,
    trackSubscriptionImpression,
} from './eventing';

const PLAN_KEYS = {
    VALUE: 'value-plan',
    PERSONAL: 'personal-plan',
    TEAM: 'team-plan',
    ENTERPRISE: 'enterprise-plan',
} as const;

type PlanKey = (typeof PLAN_KEYS)[keyof typeof PLAN_KEYS];

type PlanFeature = {
    key: string;
    contentType: 'text' | 'add-on' | 'included';
    value: boolean | string;
    note?: string | undefined;
    noteTooltip?: string | undefined;
    isLoading?: boolean;
};

/**
 * Returns the expected order of plan columns based on experiment configuration
 */
const getExpectedPlanOrder = (isValuePlanExperimentEnabled: boolean): PlanKey[] => {
    return isValuePlanExperimentEnabled
        ? [PLAN_KEYS.VALUE, PLAN_KEYS.PERSONAL]
        : [PLAN_KEYS.PERSONAL, PLAN_KEYS.TEAM, PLAN_KEYS.ENTERPRISE];
};

const InfoTooltip = ({text}: {text: string}) => (
    <div className={styles['tooltip-container']}>
        <Tooltip
            className={styles.tooltip}
            a11yRole="none"
            placement="bottom"
            trigger={<Icon glyph="info-outline" color="subdued" label="info" size="medium" />}
        >
            {text}
        </Tooltip>
    </div>
);

const PlanCTA = ({
    text,
    url,
    onClick,
    ctaStyle = 'primary',
    disabled = false,
}: {
    text: string;
    url: string;
    isLoading: boolean;
    ctaStyle?: 'primary' | 'secondary';
    disabled?: boolean;
} & Pick<ButtonProps, 'onClick'>) => {
    return (
        <Button
            id="plan-cta-button"
            componentClass="a"
            className={styles['plan-cta']}
            udStyle={ctaStyle}
            target="_blank"
            href={url}
            onClick={onClick}
            disabled={disabled}
        >
            {text} <Icon glyph="arrow-right" label={false} />
        </Button>
    );
};

const MatrixLegend = ({
    isValuePlanExperimentEnabled = false,
}: {
    isValuePlanExperimentEnabled: boolean;
}) => (
    <div className={styles.legend}>
        <div className={styles['legend-item']}>
            <span className={styles['legend-icon']}>
                <Icon
                    glyph="success"
                    label="Included"
                    color="positive"
                    size="medium"
                    aria-hidden="true"
                />
            </span>
            {gettext('Included in plan')}
        </div>
        <div className={styles['legend-item']}>
            <span className={styles['legend-icon']}>
                <Icon
                    glyph={!isValuePlanExperimentEnabled ? 'plus-circle-outline' : 'collapse-minus'}
                    label="Add on"
                    color="positive"
                    size="medium"
                    aria-hidden="true"
                />
            </span>
            {!isValuePlanExperimentEnabled
                ? gettext('Available with add-on')
                : gettext('Not included in plan')}
        </div>
    </div>
);

const MatrixCellContent = ({contentType, value, note, noteTooltip, isLoading}: PlanFeature) => {
    const includedIcon = <Icon glyph="success" label="Included" color="positive" size="medium" />;
    const addOnIcon = (
        <Icon glyph="plus-circle-outline" label="Add on" color="positive" size="medium" />
    );

    if (isLoading) {
        return <BlockSkeleton />;
    }

    switch (contentType) {
        case 'text':
            return (
                <>
                    {value}
                    {note && (
                        <div className={styles['content-note']}>
                            {noteTooltip ? (
                                <Tooltip
                                    a11yRole="none"
                                    placement="bottom"
                                    trigger={<div>{note}</div>}
                                >
                                    {noteTooltip}
                                </Tooltip>
                            ) : (
                                note
                            )}
                        </div>
                    )}
                </>
            );
        case 'add-on':
            return value ? (
                <Tooltip a11yRole="none" placement="bottom" trigger={addOnIcon}>
                    {gettext('Available with add-on')}
                </Tooltip>
            ) : null;
        case 'included':
            return value ? <>{includedIcon}</> : null;
    }
    return null;
};

export const ComparisonMatrix = () => {
    const {gettext, interpolate} = useI18n();
    const {formatNumber} = useFormatNumber();
    const {formatCurrency} = useFormatCurrency();
    const udData = useUDData();
    const udLink = useUDLink();
    const {getOrgNumericSiteStat} = useSiteStats();

    const headerRef = useRef<HTMLTableSectionElement>(null);
    const lastFeatureRef = useRef<HTMLTableRowElement>(null);
    const subheadersRef = useRef<Map<number, HTMLTableSectionElement>>(new Map());

    const [headerHeight, setHeaderHeight] = useState(0);
    const [subheaderHeight, setSubheaderHeight] = useState(0);
    const [lastFeatureHeight, setLastFeatureHeight] = useState(0);

    const [valuePlanIsLoading, setValuePlanIsLoading] = useState(true);
    const [personalPlanIsLoading, setPersonalPlanIsLoading] = useState(true);
    const [teamPlanIsLoading, setTeamPlanIsLoading] = useState(true);
    const [personalPlanTrialAvailable, setPersonalPlanTrialAvailable] = useState(true);
    const [teamPlanTrialAvailable, setTeamPlanTrialAvailable] = useState(true);

    const {getCtaText, isValuePlanSubscriber, valuePlanContext} = useValuePlanExperimentLogic();
    const personalPlanContext = useConsumerSubscriptionPlan();
    const teamPlanContext = useTeamSubscriptionPlan();

    // Determine subscriber status for CTA logic
    const isPersonalPlanSubscriber =
        personalPlanContext && 'isSubscriberToSpecificPlan' in personalPlanContext
            ? personalPlanContext.isSubscriberToSpecificPlan ?? false
            : false;

    const {isLoading: isValuePlanExperimentLoading, featureVariantAssignments} = useFeatures([
        VALUE_PLANSTARTER_PLAN,
    ]);
    const isValuePlanExperimentEnabled =
        (!isValuePlanExperimentLoading &&
            featureVariantAssignments.get(VALUE_PLANSTARTER_PLAN)?.configuration
                ?.enableValuePlan) ||
        false;

    const starterPlanCourseCount = useMemo(
        () => calculateNumCourses(valuePlanContext),
        [valuePlanContext],
    );

    const personalPlanCourseCount = useMemo(
        () => calculateNumCourses(personalPlanContext),
        [personalPlanContext],
    );

    const {data} = useComparisonMatrixData(
        gettext,
        formatNumber,
        getOrgNumericSiteStat,
        isValuePlanExperimentEnabled,
        starterPlanCourseCount,
        personalPlanCourseCount,
        personalPlanContext?.isLoading ?? true,
    );

    // Used to retrieve the proper values of state in the onScroll callback below.
    // Without this, onScroll would only be able to use the state values from when the component
    // is first instantiated.
    const headerHeightRef = useRef(headerHeight);
    const subheaderHeightRef = useRef(subheaderHeight);
    const lastFeatureHeightRef = useRef(lastFeatureHeight);

    const updateSubheaderTopStyles = (top: number) => {
        const subheaders = subheadersRef.current;
        if (serverOrClient.global.requestAnimationFrame) {
            serverOrClient.global.requestAnimationFrame(() => {
                subheaders.forEach((subheader) => {
                    subheader.style.top = top.toString() + 'px';
                });
            });
        }
    };

    const onScroll = useCallback(() => {
        const lastSubheaderElement = subheadersRef.current.get(subheadersRef.current.size - 1);
        if (lastSubheaderElement) {
            const headerHeight = headerHeightRef.current;
            const lastSubheaderHeight = subheaderHeightRef.current;
            const lastFeatureHeight = lastFeatureHeightRef.current;

            const lastSubheaderTop = parseFloat(lastSubheaderElement.style.top.slice(0, -2));
            const lastFeatureTop = lastFeatureRef.current?.getBoundingClientRect().top;

            if (lastFeatureTop) {
                // diff is how far the last feature is intruding into the space where the subheader would live if it wasn't animated,
                // where more positive is more intrusion.
                const diff = headerHeight + lastSubheaderHeight - lastFeatureTop;
                // If the last feature is actively intruding (and thus the subheaders should move)
                if (diff > 0 && lastFeatureTop + lastFeatureHeight > headerHeight) {
                    // then move the subheaders, such that the subheader will be fully hidden
                    // at the same time the feature is fully hidden.
                    updateSubheaderTopStyles(
                        headerHeight -
                            (diff / (lastSubheaderHeight + lastFeatureHeight)) *
                                lastSubheaderHeight,
                    );
                } else if (
                    Number.isNaN(lastSubheaderTop) ||
                    Math.abs(lastSubheaderTop - headerHeight) > 0.51
                ) {
                    // Otherwise, if the last subheader isn't in its default sticky position (wih some slack for float comparisons/truncation,
                    // Move it there.
                    updateSubheaderTopStyles(headerHeight);
                }
            }
        }
    }, []);

    useEffect(() => {
        const onResize = () => {
            setHeaderHeight(headerRef.current?.getBoundingClientRect().height ?? 0);
            setSubheaderHeight(subheadersRef?.current?.get(0)?.getBoundingClientRect().height ?? 0);
            setLastFeatureHeight(lastFeatureRef.current?.getBoundingClientRect().height ?? 0);
        };
        serverOrClient.global.addEventListener('resize', onResize);
        serverOrClient.global.addEventListener('scroll', onScroll);
        onResize();
        onScroll();
        return () => {
            serverOrClient.global.removeEventListener('resize', onResize);
            serverOrClient.global.removeEventListener('scroll', onScroll);
        };
    }, [onScroll]);

    useEffect(() => {
        headerHeightRef.current = headerHeight;
    }, [headerHeight]);
    useEffect(() => {
        subheaderHeightRef.current = subheaderHeight;
    }, [subheaderHeight]);
    useEffect(() => {
        lastFeatureHeightRef.current = lastFeatureHeight;
    }, [lastFeatureHeight]);

    const valueSubscription:
        | {
              subscriptionPlan: ConsumerSubscriptionValuePlan | undefined;
              priceComponent: React.ReactNode;
          }
        | undefined = useMemo(() => {
        if (!valuePlanContext && !valuePlanIsLoading) {
            return undefined;
        }
        setValuePlanIsLoading(valuePlanContext?.isLoading ?? false);
        if (isValuePlanSubscriber) {
            return {
                subscriptionPlan: valuePlanContext as ConsumerSubscriptionValuePlan,
                priceComponent: <>&nbsp;</>,
            };
        }
        if (valuePlanContext && 'priceOption' in valuePlanContext) {
            const formattedMonthlyPrice = valuePlanContext.formattedMonthlyAmount;
            const discountMonthlyPrice =
                valuePlanContext.selectedDiscount?.subscriptionDiscountMonthlyPrice;

            const currency = valuePlanContext.monthlyPlanOption?.listPrice?.currency ?? '';
            const formattedDiscountMonthlyPrice = discountMonthlyPrice
                ? formatCurrency(discountMonthlyPrice, {currency})
                : undefined;
            const formattedOriginalPrice = formatCurrency(
                valuePlanContext.monthlyPlanOption?.listPrice?.amount ?? 0,
                {currency},
            );
            return {
                subscriptionPlan: valuePlanContext,
                priceComponent: discountMonthlyPrice ? (
                    <span className={styles['price-text']}>
                        <PlanPeriod
                            discountPriceText={formattedDiscountMonthlyPrice}
                            listPriceText={formattedOriginalPrice}
                            style={PlanPeriodStyle.PLAN_COMPARISON}
                            emphasizeListPriceText={true}
                        />
                    </span>
                ) : (
                    <span className={styles['price-text']}>
                        <PlanPeriod
                            listPriceText={formattedMonthlyPrice}
                            style={PlanPeriodStyle.PLAN_COMPARISON}
                        />
                    </span>
                ),
            };
        }
        return {subscriptionPlan: undefined, priceComponent: <BlockSkeleton />};
    }, [valuePlanContext, valuePlanIsLoading, formatCurrency, isValuePlanSubscriber]);

    // The Personal Plan Price prop
    const personalSubscription = useMemo(() => {
        // Don't display card if there isn't a plan available
        if (!personalPlanContext && !personalPlanIsLoading) {
            return undefined;
        }
        setPersonalPlanIsLoading(personalPlanContext?.isLoading ?? true);
        if (isPersonalPlanSubscriber) {
            return {subscriptionPlan: personalPlanContext, priceComponent: <>&nbsp;</>};
        }
        if (personalPlanContext && 'priceOption' in personalPlanContext) {
            setPersonalPlanTrialAvailable(!!personalPlanContext?.trialPeriodDays);
            const formattedMonthlyPrice = personalPlanContext.formattedMonthlyAmount;
            const discountMonthlyPrice =
                personalPlanContext.selectedDiscount?.subscriptionDiscountMonthlyPrice;
            const formattedDiscountMonthlyPrice = discountMonthlyPrice
                ? formatCurrency(discountMonthlyPrice)
                : undefined;
            return {
                subscriptionPlan: personalPlanContext,
                priceComponent: discountMonthlyPrice ? (
                    <PlanPeriod
                        discountPriceText={formattedDiscountMonthlyPrice}
                        listPriceText={formattedMonthlyPrice}
                        style={PlanPeriodStyle.PLAN_COMPARISON}
                    />
                ) : (
                    <PlanPeriod
                        listPriceText={formattedMonthlyPrice}
                        style={PlanPeriodStyle.PLAN_COMPARISON}
                    />
                ),
            };
        }
    }, [personalPlanContext, personalPlanIsLoading, formatCurrency, isPersonalPlanSubscriber]);

    // The Team Plan Price prop
    const teamSubscription = useMemo(() => {
        // Don't display card if there isn't a plan available
        if (!teamPlanContext && !teamPlanIsLoading) {
            return null;
        }
        setTeamPlanIsLoading(teamPlanContext?.isLoading ?? true);
        if (teamPlanContext && 'priceOption' in teamPlanContext) {
            const yearlyPlanOption = teamPlanContext.priceOption(
                DateIntervalType.Year,
            ) as AnnualSubscriptionPlanPricingOption;

            setTeamPlanTrialAvailable(!!teamPlanContext?.trialPeriodDays);

            // HACK HACK HACK - this is a temporary solution to get the price to display for TPv1 b/c pricing won't support it at the GraphQL layer.
            // Specifically, the hardcoded TPv1 fallback price of $360.00 USD per year must be shown in USD, and must fully override the local currency formatter.
            // This is known to be a bad experience for users and an anti-pattern, but again, we do not have support a fallback in the API layer
            // for TPv1.
            const useFallbackPriceFormatter = teamPlanContext?.id === '0';
            const price = useFallbackPriceFormatter
                ? formatCurrency(
                      yearlyPlanOption?.listPrice.amount,
                      TEAM_PLAN_V1_FALLBACK_CURRENCY_FORMATTER,
                  )
                : formatCurrency(yearlyPlanOption.monthlyPrice.amount);
            const priceText = useFallbackPriceFormatter
                ? interpolate(gettext('%(price)s a year per user'), {price}, true)
                : interpolate(gettext('%(price)s a month per user'), {price}, true);

            return {
                subscriptionPlan: teamPlanContext,
                priceComponent: priceText,
            };
        }
    }, [teamPlanContext, teamPlanIsLoading, formatCurrency, gettext, interpolate]);

    const headcount = interpolate(
        gettext('%(min)s to %(max)s people'),
        {
            min: teamSubscription?.subscriptionPlan.minimumLicenseCount,
            max: teamSubscription?.subscriptionPlan.maximumLicenseCount,
        },
        true,
    );

    const expectedPlanOrder = getExpectedPlanOrder(isValuePlanExperimentEnabled);

    return (
        <div>
            <h2 className={'ud-heading-serif-xxl ud-text-responsive'}>
                {gettext('Compare plans')}
            </h2>
            <table className={styles.matrix}>
                <thead className={styles.sticky} ref={headerRef}>
                    <tr>
                        <th>
                            <MatrixLegend
                                isValuePlanExperimentEnabled={isValuePlanExperimentEnabled}
                            />
                        </th>
                        {isValuePlanExperimentEnabled && (
                            <th>
                                <PlanComparisonHeader
                                    borderTopColor="color-teal-150"
                                    ctaStyle="secondary"
                                    title={gettext('Starter Plan')}
                                    subtitle={
                                        valueSubscription?.subscriptionPlan ? (
                                            <TrackImpression
                                                trackFunc={() => {
                                                    if (
                                                        valueSubscription?.subscriptionPlan &&
                                                        'priceOption' in
                                                            valueSubscription.subscriptionPlan
                                                    ) {
                                                        trackSubscriptionImpression(
                                                            udData.Config?.price_country?.id ?? '',
                                                            SubscriptionPlanProductType.ConsumersubscriptionValuePlan,
                                                            valueSubscription.subscriptionPlan,
                                                        );
                                                    }
                                                }}
                                            >
                                                <div className={styles['header-subtitle']}>
                                                    {valueSubscription?.priceComponent}
                                                    {!isValuePlanSubscriber && (
                                                        <span className="ud-text-sm">
                                                            {'. '}
                                                            {gettext('Cancel anytime.')}
                                                        </span>
                                                    )}
                                                </div>
                                            </TrackImpression>
                                        ) : (
                                            valueSubscription?.priceComponent
                                        )
                                    }
                                    userDetails={
                                        <PlanCTA
                                            text={getCtaText('value', isPersonalPlanSubscriber)}
                                            url={STARTER_PLAN_CHECKOUT_URL}
                                            ctaStyle="secondary"
                                            disabled={
                                                isValuePlanSubscriber || isPersonalPlanSubscriber
                                            }
                                            onClick={() => {
                                                removeSubscribeFromPageCookie();
                                                trackSubscriptionCtaClick(
                                                    SubscriptionPlanProductType.Consumersubscription,
                                                    valueSubscription?.subscriptionPlan,
                                                );
                                            }}
                                            isLoading={valuePlanIsLoading}
                                        />
                                    }
                                />
                            </th>
                        )}
                        <th>
                            <PlanComparisonHeader
                                borderTopColor="color-purple-300"
                                title={gettext('Personal Plan')}
                                subtitle={
                                    personalSubscription?.subscriptionPlan ? (
                                        <TrackImpression
                                            trackFunc={() => {
                                                if (
                                                    personalSubscription?.subscriptionPlan &&
                                                    'priceOption' in
                                                        personalSubscription.subscriptionPlan
                                                ) {
                                                    trackSubscriptionImpression(
                                                        udData.Config?.price_country?.id ?? '',
                                                        SubscriptionPlanProductType.Consumersubscription,
                                                        personalSubscription.subscriptionPlan,
                                                    );
                                                }
                                            }}
                                        >
                                            <div className={styles['header-subtitle']}>
                                                {personalSubscription.priceComponent}
                                                {isValuePlanExperimentEnabled &&
                                                    !isPersonalPlanSubscriber && (
                                                        <span className="ud-text-sm">
                                                            {'. '}
                                                            {gettext('Cancel anytime.')}
                                                        </span>
                                                    )}
                                            </div>
                                        </TrackImpression>
                                    ) : (
                                        personalSubscription?.priceComponent
                                    )
                                }
                                userDetails={
                                    <>
                                        {!isValuePlanExperimentEnabled && (
                                            <div className={styles['user-detail']}>
                                                <Icon
                                                    glyph="person"
                                                    color="subdued"
                                                    label={false}
                                                />{' '}
                                                {gettext('Individual')}
                                            </div>
                                        )}
                                        <PlanCTA
                                            text={
                                                isValuePlanExperimentEnabled
                                                    ? getCtaText(
                                                          'personal',
                                                          isPersonalPlanSubscriber,
                                                      )
                                                    : personalPlanTrialAvailable
                                                      ? gettext('Try it free')
                                                      : gettext('Start subscription')
                                            }
                                            url={
                                                isValuePlanExperimentEnabled
                                                    ? isValuePlanSubscriber
                                                        ? PERSONAL_PLAN_UPGRADE_URL
                                                        : PERSONAL_PLAN_CHECKOUT_URL
                                                    : udLink.to('personal-plan')
                                            }
                                            disabled={
                                                isValuePlanExperimentEnabled &&
                                                isPersonalPlanSubscriber
                                            }
                                            onClick={() => {
                                                removeSubscribeFromPageCookie();
                                                if (
                                                    personalSubscription?.subscriptionPlan &&
                                                    'priceOption' in
                                                        personalSubscription.subscriptionPlan
                                                ) {
                                                    trackSubscriptionCtaClick(
                                                        SubscriptionPlanProductType.Consumersubscription,
                                                        personalSubscription.subscriptionPlan,
                                                    );
                                                }
                                            }}
                                            isLoading={personalPlanIsLoading}
                                        />
                                    </>
                                }
                            />
                        </th>
                        {!isValuePlanExperimentEnabled && (
                            <th>
                                <PlanComparisonHeader
                                    title={gettext('Team Plan')}
                                    subtitle={
                                        teamSubscription?.subscriptionPlan ? (
                                            <TrackImpression
                                                trackFunc={() => {
                                                    trackSubscriptionImpression(
                                                        udData.Config?.price_country?.id ?? '',
                                                        SubscriptionPlanProductType.Team,
                                                        teamSubscription?.subscriptionPlan,
                                                    );
                                                    trackOrganizationImpression(
                                                        getComparisonMatrixHeaderData(udData)
                                                            .teamPlan.ctaUrl,
                                                        udData.request.locale,
                                                    );
                                                }}
                                            >
                                                <div className={styles['header-subtitle']}>
                                                    {teamSubscription?.priceComponent}
                                                </div>
                                            </TrackImpression>
                                        ) : (
                                            teamSubscription?.priceComponent
                                        )
                                    }
                                    ctaStyle="secondary"
                                    userDetails={
                                        <>
                                            <div className={styles['user-detail']}>
                                                <>
                                                    <Icon
                                                        glyph="people"
                                                        color="subdued"
                                                        label={false}
                                                    />{' '}
                                                    {teamSubscription?.subscriptionPlan
                                                        .minimumLicenseCount && headcount}
                                                </>
                                            </div>
                                            <PlanCTA
                                                text={
                                                    teamPlanTrialAvailable
                                                        ? gettext('Try it free')
                                                        : gettext('Start subscription')
                                                }
                                                url={
                                                    getComparisonMatrixHeaderData(udData).teamPlan
                                                        .ctaUrl
                                                }
                                                onClick={() => {
                                                    trackSubscriptionCtaClick(
                                                        SubscriptionPlanProductType.Team,
                                                        teamSubscription?.subscriptionPlan,
                                                    );
                                                    trackOrganizationCtaClickEvent(
                                                        getComparisonMatrixHeaderData(udData)
                                                            .teamPlan.ctaUrl,
                                                        udData.request.locale,
                                                    );
                                                }}
                                                isLoading={teamPlanIsLoading}
                                            />
                                        </>
                                    }
                                />
                            </th>
                        )}
                        {!isValuePlanExperimentEnabled && (
                            <th>
                                <PlanComparisonHeader
                                    title={gettext('Enterprise Plan')}
                                    subtitle={
                                        <div className={styles['header-subtitle']}>
                                            {gettext('Contact sales for pricing')}
                                        </div>
                                    }
                                    userDetails={
                                        <TrackImpression
                                            trackFunc={() =>
                                                trackOrganizationImpression(
                                                    getComparisonMatrixHeaderData(udData)
                                                        .enterprisePlan.ctaUrl,
                                                    udData.request.locale,
                                                )
                                            }
                                        >
                                            <div>
                                                <div className={styles['user-detail']}>
                                                    <Icon
                                                        glyph="people"
                                                        color="subdued"
                                                        label={false}
                                                    />{' '}
                                                    {gettext('More than 20 people')}
                                                </div>
                                                <PlanCTA
                                                    text={gettext('Request a demo')}
                                                    url={
                                                        getComparisonMatrixHeaderData(udData)
                                                            .enterprisePlan.ctaUrl
                                                    }
                                                    onClick={() =>
                                                        trackOrganizationCtaClickEvent(
                                                            getComparisonMatrixHeaderData(udData)
                                                                .enterprisePlan.ctaUrl,
                                                            udData.request.locale,
                                                        )
                                                    }
                                                    isLoading={false}
                                                />
                                            </div>
                                        </TrackImpression>
                                    }
                                />
                            </th>
                        )}
                    </tr>
                </thead>
                {data.map((section, index) => {
                    return (
                        <React.Fragment key={index}>
                            <thead
                                className={styles.subhead}
                                ref={(node) => {
                                    const subheaders = subheadersRef.current;
                                    if (node) {
                                        subheaders.set(index, node);
                                    } else {
                                        subheaders.delete(index);
                                    }
                                }}
                            >
                                <tr>
                                    <th colSpan={4}>{section.title}</th>
                                </tr>
                            </thead>
                            <tbody className={styles.section}>
                                {section.features.map((feature, featureIndex) => {
                                    const isLastFeature =
                                        index === data.length - 1 &&
                                        featureIndex === section.features.length - 1;
                                    const ref = isLastFeature ? lastFeatureRef : undefined;

                                    const planMap = new Map(
                                        feature.plans.map((plan) => [plan.key, plan]),
                                    );

                                    return (
                                        <tr key={featureIndex} ref={ref}>
                                            <td className={styles.feature}>
                                                {feature.title}
                                                {'tooltip' in feature && feature.tooltip && (
                                                    <InfoTooltip text={feature.tooltip} />
                                                )}
                                            </td>
                                            {expectedPlanOrder.map((planKey) => {
                                                const planData = planMap.get(planKey);
                                                return (
                                                    <td key={planKey}>
                                                        {planData && (
                                                            <MatrixCellContent
                                                                {...(planData as PlanFeature)}
                                                            />
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </React.Fragment>
                    );
                })}
            </table>
        </div>
    );
};
